import { useEffect, useMemo } from 'react';
import { normalizeEmail } from '@/utils/normalizeEmail';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { mediaServiceApi } from '@store/api';
import type { RootState } from '@store/index';
import type { PlannerEventRow } from '@/types/planner.types';
import type { OwnerPhotoCacheEntry } from './ownerPhotoCache';

const UNASSIGNED_OWNER_NAME = 'unassigned';

function collectOwnerEmails(events: readonly PlannerEventRow[]): string[] {
  const seen = new Set<string>();
  for (const event of events) {
    const owner = event.owner;
    if (!owner) continue;
    if (owner.name && owner.name.trim().toLowerCase() === UNASSIGNED_OWNER_NAME) continue;
    const normalized = normalizeEmail(owner.email);
    if (!normalized) continue;
    seen.add(normalized);
  }
  return Array.from(seen);
}

const selectEmployeePhoto = mediaServiceApi.endpoints.getEmployeePhoto.select;

function buildPhotoMap(
  state: RootState,
  emails: readonly string[]
): ReadonlyMap<string, OwnerPhotoCacheEntry> {
  const map = new Map<string, OwnerPhotoCacheEntry>();
  for (const email of emails) {
    const result = selectEmployeePhoto(email)(state);
    if (result.isUninitialized || result.isLoading) {
      map.set(email, { status: 'pending' });
    } else if (result.isError) {
      // Transient failures are NOT cached as a miss so the next batch can retry.
      continue;
    } else if (result.data?.dataUrl) {
      map.set(email, { status: 'hit', dataUrl: result.data.dataUrl });
    } else {
      map.set(email, { status: 'miss' });
    }
  }
  return map;
}

function ownerPhotoMapsEqual(
  a: ReadonlyMap<string, OwnerPhotoCacheEntry>,
  b: ReadonlyMap<string, OwnerPhotoCacheEntry>
): boolean {
  if (a.size !== b.size) return false;
  for (const [key, entryA] of a) {
    const entryB = b.get(key);
    if (!entryB || entryB.status !== entryA.status) return false;
    if (entryA.status === 'hit' && entryB.status === 'hit' && entryA.dataUrl !== entryB.dataUrl) {
      return false;
    }
  }
  return true;
}

/**
 * Fetches and caches profile photos for the supplied event owners (feature 090, store-backed
 * via feature 107).
 *
 * Delegates the actual network call to the store's `mediaService.getEmployeePhoto` endpoint
 * (one cached request per unique owner email). The store's RTK Query cache survives
 * filter/sort/refetch resets in `usePlannerEvents`, so those do NOT trigger refetches
 * (FR-006, SC-002, SC-004).
 *
 * Returns a snapshot Map keyed by normalized email. Consumers MUST normalize their
 * lookup key via {@link normalizeEmail}.
 */
export function useOwnerPhotos(
  events: readonly PlannerEventRow[]
): ReadonlyMap<string, OwnerPhotoCacheEntry> {
  const dispatch = useAppDispatch();

  const emailsKey = useMemo(() => collectOwnerEmails(events).join('|'), [events]);
  const emails = useMemo(() => (emailsKey ? emailsKey.split('|') : []), [emailsKey]);

  useEffect(() => {
    if (emails.length === 0) return undefined;
    const subscriptions = emails.map((email) =>
      dispatch(mediaServiceApi.endpoints.getEmployeePhoto.initiate(email))
    );
    return () => {
      subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    };
  }, [dispatch, emails]);

  return useAppSelector((state) => buildPhotoMap(state, emails), ownerPhotoMapsEqual);
}
